import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { validationRules } from '../custom-datatable/validation';
import { UploadService } from 'src/app/components/expense/services/upload.service';
import { lastValueFrom } from 'rxjs';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { EventService } from 'src/app/services/event.service';
import { ProjectFileService } from 'src/app/services/projectFiles.service';
import { SharedProjectService } from 'src/app/components/projects/components/project/project.component';
import { UploadHandlerService } from 'src/app/shared/uploadFileHandler.service';

@Component({
  selector: 'app-custom-editable-grid',
  templateUrl: './custom-editable-grid.component.html',
  styleUrls: ['./custom-editable-grid.component.scss']
})
export class CustomEditableGridComponent implements OnInit {
  @Input() showImportButton: boolean = false;
  @Input() showAdjustColumns: boolean = false;
  @Input() schemaName: string = '';
  @Input() isVerticalTable: boolean = false;
  @Input() isAdjustive: boolean = false;
  @Input() columns: any[] = [];
  @Input() defaultData: any = {};
  @Input() verticleTableHeaderTitle: string = '';
  @Input() onCellRender: Function;
  @Input() data: any[] = [];
  @Input() showImportFileButton: boolean = false;
  @Input() subjectPropertyId: any;

  @Output() dataChange = new EventEmitter<any>();

  editingColumnIndex: number | null = null;
  newRow: any = {};
  errors: any = {};
  isEditingAll = false; // Tracks whether all fields are editable
  isSettingsOpen: boolean = false;
  columnsCopy: any[] = [];
  tableVerticleView: boolean = false;
  showNewInputRow: boolean = false
  editingRow: any;
  rowToDelete: any;
  isImportModalOpen = false;
  isDeleteModalOpen: boolean = false;
  objectKeys = Object.keys;
  isUploading: boolean = false;
  displayedColumns: any = [];
  @Input() properties: any = [];
  @Output() filterApplied = new EventEmitter<any>();
  @Output() addComps = new EventEmitter<any>();
  selectedComps: any=[];
  newRows: any[] = [{}]; // Start with one empty row
  @Input() locationCoordinates: any = [];
  @Input() showMap : boolean = false;
  @Input() totalCount: number = 0;
  @Input() pageSize: number = 10;
  @Input() offset: number = 0;
  @Input() currentPage: number = 1;
  @Input() propertyDetail: any;
  @Output() pageChange = new EventEmitter<number>();

  enableSummary: boolean = false;
  enableAdjustment: boolean = false;

  projectId: any = null;
  @Output() loadMore = new EventEmitter<any>();

  constructor(
    private uploadService: UploadService,
    private broadcaster: EventService,
    private projectFileService: ProjectFileService,
    private sharedProjectService: SharedProjectService,
    private uploadHandler:UploadHandlerService
  ) { }

  ngOnInit() {
    this.tableVerticleView = this.isVerticalTable;
    this.projectId = this.sharedProjectService.project.id;
    if (this.data && this.columns)
      this.fetchFilesData()
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && changes['data'].currentValue?.length) {
  
      this.locationCoordinates = this.data.reduce((acc, p) => {
        if (p.lat && p.lng) {
          acc.push({
            lat: Number(p.lat),
            lng: Number(p.lng)
          });
        }
        return acc;
      }, []);
  
      if (this.propertyDetail?.latitude && this.propertyDetail?.longitude) {
        this.locationCoordinates.push({
          lat: Number(this.propertyDetail.latitude),
          lng: Number(this.propertyDetail.longitude)
        });
      }
  
    }
  }
  
  assignAndSortColumns(columns: any[]): any[] {
    // Assign missing indexes
    const withIndexes = columns.map((col, i) => {
      return {
        ...col,
        index: (col.index !== undefined && !isNaN(Number(col.index))) ? Number(col.index) : i
      };
    });

    // Sort by index
    return withIndexes.sort((a, b) => a.index - b.index);
  }

  formatCurrency(value: number, locale: string = 'en-US', currency: string = 'USD'): string {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(value && !isNaN(value) ? value : 0);
  }


  formatNumber(value: number, locale: string = 'en-US'): string {
    return new Intl.NumberFormat(locale).format(value);
  }


  renderCell(data) {
    return this.onCellRender(data);
  }

  startEdit(index: number) {
    this.editingRow = index;
  }

  // Start Editing a Column (All Rows in that Column)
  startEditCol(colIndex: number) {
    this.editingColumnIndex = colIndex;
  }


  startEditAll() {
    this.isEditingAll = true;
  }

  validateField(field: string, type: string, index?: any, data?: any, isNewRow?: boolean) {
    const column = this.columns.find(col => col.field === field);
    const value = data[field]
    this.errors = this.errors || { newRows: [] };

    let errorMessage = null;

    if (column && column.validations) {
      errorMessage = this.applyValidations(value, column.name, column.validations);
    }

    if (!errorMessage && column && column.type === 'number' && value < 0) {
      errorMessage = `${column.name} must be a positive number.`;
    }

    // Set the error in the right location
    if (index !== undefined) {
      if (isNewRow) {
        this.errors.newRows = this.errors.newRows || [];
        this.errors.newRows[index] = {
          ...(this.errors.newRows[index] || {}),
          [field]: errorMessage
        };
      } else {
        this.errors[index] = {
          ...(this.errors[index] || {}),
          [field]: errorMessage
        };
      }
    } else {
      this.errors[field] = errorMessage;
    }

    // ✅ Push a new empty row if the last new row is now valid
    if (isNewRow && index === this.newRows.length - 1) {
      const currentRowErrors = this.errors?.newRows?.[index];
      const isRowValid = !currentRowErrors || Object.values(currentRowErrors).every(err => !err);

      const currentRow = this.newRows[index];
      const hasAnyValue = Object.values(currentRow ?? {}).some(
        val => val !== null && val !== undefined && val !== ''
      );

      if (isRowValid && hasAnyValue) {
        this.newRows.push({});
      }
    }
  }


  applyValidations(value: any, fieldTitle: string, validations: string[]) {
    for (const rule of validations) {
      const message = validationRules[rule](value, fieldTitle);
      if (message !== true) {
        return message;
      }
    }
    return null;

  };

  fileState = {}

  fetchFilesData() {
    const imageFields = this.columns
      .filter(col => col.type === "image")
      .map(col => col.field);

    for (const item of this.data) {
      for (const field of imageFields) {
        if (item[field]) {
          const images = Array.isArray(item[field]) ? item[field] : [item[field]];
          this.projectFileService.projectFilesByIds(images).subscribe((res) => {
            item[field] = res.data.projectFilesByIds;
          })
        }
      }
    }
  }

  getFilesToUpload(event, field, index) {
    if (!this.fileState[field]) {
      this.fileState[field] = {
        [index]: event
      }
    }
    else {
      this.fileState[field][index] = event;
    }
  }

  async saveAllEdits() {
    const rowsToValidate = this.newRows.slice(0, -1);
    const updatedRows = [...this.data];
    this.errors = { newRows: [] };
    let isValid = true;
    const filteredColumns = this.columns.filter(col => col.visible === true || col.visible === 'true');

    this.data.forEach((row, rowIndex) => {
      this.errors[rowIndex] = {};
      filteredColumns.forEach(col => {
        this.validateField(col.field, col.type, rowIndex, row);
        if (this.errors[rowIndex][col.field]) {
          isValid = false;
        }
      });
    });
    const validNewRows = [];
    rowsToValidate.forEach((newRow, newIndex) => {
      const hasValues = Object.values(newRow ?? {}).some(
        value => value !== null && value !== undefined && value !== ''
      );

      if (!hasValues) return;

      this.errors.newRows[newIndex] = {};
      filteredColumns.forEach(col => {
        this.validateField(col.field, col.type, newIndex, newRow, true);
        if (this.errors.newRows[newIndex][col.field]) {
          isValid = false;
        }
      });

      validNewRows.push(newRow);
    });

    if (!isValid) {
      return;
    }

    const allData = [...updatedRows, ...validNewRows];

    // ==================================================================================================
    // Call the upload file method for the current fileState being prepared.
    await this.uploadFiles(this.fileState);

    const fileColumns = Object.keys(this.fileState);
    for (let i = 0; i < allData.length; i++) {
      const matchingKeys = fileColumns.filter(key => key in allData[i]);

      if (matchingKeys.length > 0) {

        for (let key of matchingKeys) {
          allData[i][key] = this.fileState[key][i];
        }
      }
    }
    // ==================================================================================================

    this.dataChange.emit({
      data: allData.length ? allData : [],
      type: this.data?.length ? 'edit' : 'add',
      schemaName: this.schemaName
    });

    // Reset new rows with a single empty row again
    this.newRows = [{}];
    this.isEditingAll = false;
  }

  async uploadFiles(data) {
    const imagePayloads = {}
    for (let col in data) {
      for (let key in data[col]) {
        imagePayloads[key] = await this.uploadFilesToAws(data[col][key]?.filesToUpload || [], data[col][key]?.selectedImages || []);
      }

      for (let key in imagePayloads) {
        const images = imagePayloads[key];
        let ids = []
        const payload = [];
        for (let image of images) {
          if (!image.id) {

            payload.push({
              projectId: this.projectId,
              displayName: image.name,
              fileName: image.name,
              fileType: image.mimeType,
              url: image.fileUrl,
              objectId: image.name,
              bucketFolder: 'gallery',
              group: this.schemaName,
              type: 'file',
              parentId: null,
            })
          }
          else {
            ids.push(image.id);
          }
        }

        const res = await this.projectFileService.bulkUploadProjectFiles(payload).toPromise();
        const mappedIds = res.data.bulkUploadProjectFiles.map((file) => file.id);

        ids = [...ids, ...mappedIds]
        this.fileState[col][key] = ids;
      }
    }
  }

  async uploadFilesToAws(filesToUpload, selectedFiles) {
    const imagesPayload = [];
    const folderName = 'gallery'
    try {
      if (filesToUpload.length > 0) {
        // Request upload URLs from AWS

        const response = await lastValueFrom(this.uploadService.getUploadUrl(filesToUpload.length, folderName));
        if (response.success) {
          const uploadUrls = response.success.data;
          // Upload files to AWS
          for (let i = 0; i < uploadUrls.length; i++) {
            const fileUploadUrl = uploadUrls[i].urls[0];
            const file = filesToUpload[i];
            this.broadcaster.BroadcastEvent("LOADER_EVENT", "show")
            await lastValueFrom(this.uploadService.uploadFile(file.file, fileUploadUrl));
            this.broadcaster.BroadcastEvent("LOADER_EVENT", "hide")
            imagesPayload.push({
              name: uploadUrls[i].name,
              mimeType: file?.file?.type,
              folderName: folderName,
              fileUrl: fileUploadUrl.split('?')[0]
            });
          }
        } else {
          console.error('Error fetching upload URLs');
        }
      }
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      for (let i = 0; i < selectedFiles?.length; i++) {
        const isFileExists = filesToUpload?.some((file) => file.name == selectedFiles[i].name) || null;
        if (!isFileExists) {
          imagesPayload.push({
            id: selectedFiles[i].id,
            name: selectedFiles[i].name,
            mimeType: null,
            source: selectedFiles[i].source,
            folderName: folderName,
            fileUrl: selectedFiles[i]?.fileUrl
          })
        }
      }
      return imagesPayload;
    }
  }



  cancelEditAll() {
    this.isEditingAll = false;
    this.columnsCopy = JSON.parse(JSON.stringify(this.columns));
    this.errors = {};
  }

  deleteRow(index: number) {
    this.dataChange.emit({ data: index, type: 'delete', schemaName: this.schemaName });
  }



  openDeleteModal(row: any, isNewRow: boolean = false) {
    this.rowToDelete = row;
    if (isNewRow) {
      const index = row;
      this.newRows.splice(index, 1)
    }
    else {
      this.data.splice(this.rowToDelete, 1)
    }
  }

  confirmDelete() {
    if (this.rowToDelete !== undefined) {
      this.data.splice(this.rowToDelete, 1)
      // this.deleteRow(this.rowToDelete.id);
    }
    this.isDeleteModalOpen = false;
    this.rowToDelete = null;
  }


  openSettings() {
    this.isSettingsOpen = true;
    if (this.columns && Array.isArray(this.columns)) {
      this.columnsCopy = JSON.parse(JSON.stringify(this.columns));
    }
    this.tableVerticleView = this.isVerticalTable;
  }

  applySettings(event) {
    this.dataChange.emit(event);
    this.isSettingsOpen = false;
  }

  openImportModal() {
    this.isImportModalOpen = true;
  }

  closeImportModal() {
    this.isImportModalOpen = false;
  }


  async handleFileUpload(event: Event) {
    this.broadcaster.BroadcastEvent("LOADER_EVENT", "show")
    this.uploadHandler.handleFileUpload(
      event,
      this.propertyDetail && this.propertyDetail.id ? this.propertyDetail?.id : this.subjectPropertyId,
      this.schemaName,
      async () => {
        this.dataChange.emit({ data: this.data, type: 'import', schemaName: this.schemaName });
        this.broadcaster.BroadcastEvent("LOADER_EVENT", "hide")

      },
      (err) => {
        console.error(err);
        this.broadcaster.BroadcastEvent("LOADER_EVENT", "hide")

      }
    );
  }

  onColumnDrop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex);
    }

  }

  onFilterApply(filters: any) {
    this.filterApplied.emit(filters);
  }

  getSelectedComps(comps) {
    this.selectedComps = comps
  }

  addComparable() {
    this.addComps.emit(this.selectedComps);
    this.closeImportModal()

  }



  onPageChanged(newPage: number) {
    this.currentPage = newPage;
    this.pageChange.emit(newPage);
  }

  onFieldBlur(field: string, type: string, index: number, row: any) {
    this.validateField(field, type, index, row, true);
  }

  fetchMore(){
    this.loadMore.emit()
  }


}